import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProcessingPageNew } from './ProcessingPageNew';
import { useComparisonStatus } from '../hooks/useComparisonStatus';
import { fetchResponses } from '../lib/api/results';
import { logger } from '../lib/logging';

export const ProcessingPage: React.FC = () => {
  const { comparisonId: idOrSlug } = useParams<{ comparisonId: string }>();
  const navigate = useNavigate();

  // Use status polling hook to get real-time comparison status
  // Note: idOrSlug can be either UUID or slug - backend handles both formats
  const { status: comparisonStatus, isLoading, error } = useComparisonStatus({
    comparisonId: idOrSlug,
    pollInterval: 3000,
    enabled: !!idOrSlug
  });

  // Calculate progress from real API data
  const progress = comparisonStatus
    ? (comparisonStatus.completedModels / comparisonStatus.totalModels) * 100
    : 0;

  // Track redirect state to prevent multiple concurrent attempts
  const redirectAttemptedRef = useRef(false);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 10; // Max 15 seconds of retries (1.5s per retry)

  // Auto-redirect to results page when comparison is completed
  // First validates that responses are available to prevent blank detail views
  useEffect(() => {
    if (comparisonStatus?.status === 'completed' && idOrSlug && !redirectAttemptedRef.current) {
      redirectAttemptedRef.current = true;

      const validateAndRedirect = async () => {
        try {
          // Fetch responses to verify they're available before navigating
          const correlationId = `processing_validate_${comparisonStatus.id}_${Date.now()}`;
          const responsesData = await fetchResponses(comparisonStatus.id, { correlationId });

          if (responsesData.responses && responsesData.responses.length > 0) {
            // Responses are available, safe to navigate
            logger.info('processing_validation_success', {
              comparisonId: comparisonStatus.id,
              responseCount: responsesData.responses.length,
              retryCount: retryCountRef.current
            }, {
              correlationId,
              op: 'processing_validation_success',
              component: 'ProcessingPage',
              success: true
            });

            // FIXED: Do NOT clear the response cache before redirect
            // Clearing cache forces a network refetch on the detail view, causing loading delays
            // Instead, let the cache serve responses immediately (TTL: 30s in-memory, 24h IndexedDB)
            // Only clear the comparisons list cache to ensure new comparison appears in list
            // clearCache(); // REMOVED: This was causing response loading delays

            const redirectUrl = `/results/${comparisonStatus.id}`;
            navigate(redirectUrl, { state: { fromProcessing: true } });
            return;
          }

          // No responses yet, retry after a delay
          if (retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current++;
            logger.debug('processing_validation_retry', {
              comparisonId: comparisonStatus.id,
              retryCount: retryCountRef.current,
              maxRetries: MAX_RETRIES
            }, {
              correlationId,
              op: 'processing_validation_retry',
              component: 'ProcessingPage'
            });

            const retryTimeout = setTimeout(validateAndRedirect, 1500);
            return () => clearTimeout(retryTimeout);
          }

          // Max retries exceeded, log warning and navigate anyway
          logger.warn('processing_validation_max_retries', {
            comparisonId: comparisonStatus.id,
            retryCount: retryCountRef.current
          }, {
            correlationId,
            op: 'processing_validation_max_retries',
            component: 'ProcessingPage'
          });

          // FIXED: Do NOT clear cache before redirect (causes response loading delays)
          const redirectUrl = `/results/${comparisonStatus.id}`;
          navigate(redirectUrl, { state: { fromProcessing: true } });
        } catch (err) {
          // Network or API error - still navigate but log the error
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          logger.warn('processing_validation_error', {
            comparisonId: comparisonStatus.id,
            error: errorMessage,
            retryCount: retryCountRef.current
          }, {
            op: 'processing_validation_error',
            component: 'ProcessingPage',
            err_message: errorMessage
          });

          // FIXED: Do NOT clear cache before redirect (causes response loading delays)
          const redirectUrl = `/results/${comparisonStatus.id}`;
          navigate(redirectUrl, { state: { fromProcessing: true } });
        }
      };

      // Wait 1 second to show 100% progress before validating responses
      const timeout = setTimeout(validateAndRedirect, 1000);
      return () => clearTimeout(timeout);
    }
  }, [comparisonStatus?.status, comparisonStatus?.id, idOrSlug, navigate]);

  // Show loading state while fetching comparison data
  if (isLoading && !comparisonStatus) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-slate-300">Loading comparison...</p>
        </div>
      </div>
    );
  }

  // Show error state if polling failed
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
            <div className="text-red-400 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-red-300 mb-2">Error Loading Comparison</h2>
            <p className="text-slate-300 mb-4">{error}</p>
            <button
              onClick={() => navigate('/compare')}
              className="px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 transition-colors"
            >
              Back to Compare
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no idOrSlug provided
  if (!idOrSlug) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-lg p-6 text-center">
            <div className="text-slate-400 text-5xl mb-4">🤔</div>
            <h2 className="text-xl font-semibold text-slate-300 mb-2">No Comparison Found</h2>
            <p className="text-slate-400 mb-4">Please create a new comparison to get started.</p>
            <button
              onClick={() => navigate('/compare')}
              className="px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 transition-colors"
            >
              Create Comparison
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Always use new ProcessingPageNew component
  return (
    <ProcessingPageNew
      comparisonId={comparisonStatus?.id || idOrSlug}
      title={comparisonStatus?.title}
      prompt={comparisonStatus?.prompt}
      modelsUsed={comparisonStatus?.modelsUsed}
      status={comparisonStatus?.status}
      completedModels={comparisonStatus?.completedModels}
      totalModels={comparisonStatus?.totalModels}
    />
  );
};
